import { allPass, any, complement, filter, pipe, propEq, isEmpty, isNil, memoizeWith } from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { getDiscountDisplayName } from '../../../utils/discount'
import {
  getConnectionFee,
  getEasyRoamOffers,
  getMobilityOffersWithItems,
  getMobilityPlan,
  getVASAddOns,
  getConnectionFeeAttribute,
  getMobVASAttributeList,
  getItemRecurringCredits,
  getItemNonRecurringDiscounts,
  getItemRecurringDiscounts,
  getItemsNonRecurringDiscounts,
  getOrbitDeviceAttribute,
  getLastMobilityAddon,
  getOrbitItemFromOffer,
  getSimItem,
  getSimTypeAttribute
} from '../../../utils/mobility'
import { formatCurrency } from '../../../utils/currency'
import { buildConnectionFeeData } from '../../../utils/display'
import { getCacheKey } from '../../../utils/cart'

const { _getItems, getDisplayName, getMonthlyItemPrice } = utils.cart

const isABYODDevice = allPass([
  any(propEq('category', 'MOBILITY_SIM')),
  complement(any(propEq('category', 'MOBILITY_PHONE'))),
  complement(any(propEq('category', 'MOBILITY_TABLET')))
])

/**
 * @param {Object} cart - Cart response
 * @return {Object[]} A list of BYOD mobility offers with sub-items data
 */
export const getBYODOffers = memoizeWith(
  getCacheKey('BYOD'),
  pipe(getMobilityOffersWithItems, filter(pipe(_getItems, isABYODDevice)))
)

export const getAttributes = (offer, translate, lang) => {
  const attributesList = []

  const connectionFee = getConnectionFee(offer)
  const easyRoam = getEasyRoamOffers(offer)
  const plan = getMobilityPlan(offer)
  const mobilityVAS = getVASAddOns(offer)
  const orbitOffer = getOrbitItemFromOffer(offer)
  const simItem = getSimItem(offer)

  // Plan
  const easyRoamLabel = isEmpty(easyRoam) ? '' : `, ${translate('item.easyRoam')}`
  const planNameLabel = getDisplayName(plan) + easyRoamLabel
  attributesList.push(planNameLabel)

  // SIM
  const simTypeAttribute = simItem && getSimTypeAttribute(simItem, offer, lang, translate)
  if (simTypeAttribute) attributesList.push(simTypeAttribute)

  // Connection Fee
  if (connectionFee) attributesList.push(getConnectionFeeAttribute(connectionFee, translate))

  // VAS
  attributesList.push(...getMobVASAttributeList(mobilityVAS, translate))

  // Orbit
  if (orbitOffer) attributesList.push(...getOrbitDeviceAttribute(orbitOffer, translate))

  return attributesList
}

const buildDiscountMapping = (discounts, lang, item = {}, recurring = true) => {
  const recurringSuffix = recurring ? '/mo.' : ''

  const mappedDiscountDataList = discounts?.map((discount) => {
    const id = discount.id || discount.promotionId || item.id
    const hasCouponCode = discount.isCouponCode
    const prefixIcon = hasCouponCode ? 'PIGGYBANK' : 'DEALS'
    return {
      id,
      name: getDiscountDisplayName(discount),
      price: `-${formatCurrency(discount.amount, lang)}${recurringSuffix}`,
      prefixIcon
    }
  })

  return mappedDiscountDataList
}

export const buildRecurringDiscountList = (offer, plan, lang) => {
  const offerDiscounts = getItemRecurringDiscounts(offer)
  const planDiscounts = getItemRecurringDiscounts(plan)
  const mappedOfferDiscounts = buildDiscountMapping(offerDiscounts, lang, offer)
  const mappedPlanDiscounts = buildDiscountMapping(planDiscounts, lang, plan)
  return [...mappedPlanDiscounts, ...mappedOfferDiscounts]
}

const buildNonVASMobilityAddon = (offer, translate, lang) => {
  const addon = getLastMobilityAddon(offer)

  if (!addon) return []

  return [
    {
      id: addon.id,
      name: translate('item.mobilityAddOn'),
      price: formatCurrency(getMonthlyItemPrice(addon).amount, lang)
    }
  ]
}

export const buildOtherChargesList = (offer, translate, lang) => {
  const connectionFee = buildConnectionFeeData(offer, translate, lang)
  const nonVasAddons = buildNonVASMobilityAddon(offer, translate, lang)
  return [connectionFee, ...nonVasAddons]
}

export const buildCreditsAndDiscountsList = (offer, plan, lang) => {
  const mobilityVASAddons = getVASAddOns(offer)
  const planNonRecurringDiscounts = getItemNonRecurringDiscounts(plan)
  const planRecurringCredits = getItemRecurringCredits(plan)
  const offerRecurringCredits = getItemRecurringCredits(offer)
  const nonRecurringVASDiscounts = getItemsNonRecurringDiscounts(mobilityVASAddons)

  const creditsAndDiscounts = [
    ...planNonRecurringDiscounts,
    ...planRecurringCredits,
    ...offerRecurringCredits,
    ...nonRecurringVASDiscounts
  ]

  return buildDiscountMapping(creditsAndDiscounts, lang, {}, false)
}

export const shouldRender = (cart) => {
  const offers = getBYODOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}
